import { invariant } from '@epic-web/invariant';
import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { AppExtraArgument, AppState } from '@src/providers/AppStateProvider/types';
import {
  globalToastActions,
  globalToastsDispatch,
  logger,
  ModeEnum,
  NumericField,
  QuoteStatusEnum,
  resolvePricingModeDisplaySpec,
  Unit,
  type CustomerQuote,
} from '@talos/kyoko';
import type { WritableDraft } from 'immer';
import type { NavigateFunction } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import { getManualRFQPricingRoute } from '../../../containers/Routes/routes';
import {
  createDestroyKeyedStateAction,
  keyedActionWithInstanceID,
  sliceToKeyedReducer,
} from '../../../providers/AppStateProvider/keyedReducer';
import { streamingDataSlice } from '../../../providers/AppStateProvider/streamingDataSlice';
import { OMSSliceActions } from '../OMSSlice';
import { OMSView } from '../OMSView';
import type { EditableSide, ManualRFQPricingState, PricingMethod } from './types';
import { customerQuoteToBidAskFields, getPricingMethodFromQuote, getSidesToDisable } from './utils';

/** The drawer itself is a singleton. There is only one. Drawers are not created with the ID, so we hardcode the ID here and use that. */
export const DRAWER_RFQ_PRICING_INSTANCE_ID = 'drawer-rfq-pricing';

function getInitialInstanceState(): ManualRFQPricingState {
  return {
    form: {
      pricingMethod: 'composite',
      compositeBidAsk: {
        symbol: '',
        bid: new NumericField({ name: 'Customer Sell Price', isRequired: true, unit: Unit.Decimal }),
        ask: new NumericField({ name: 'Customer Buy Price', isRequired: true, unit: Unit.Decimal }),
        bidQty: new NumericField({ name: 'Customer Sell Qty', isRequired: false, unit: Unit.Decimal }),
        askQty: new NumericField({ name: 'Customer Buy Qty', isRequired: false, unit: Unit.Decimal }),
      },
      legBidAsks: [],
    },
    isLoading: false,
    initialized: false,
    currentCustomerQuote: undefined,
    currentCustomerQuoteRFQID: undefined,
  };
}

/**
 * Disable all bid and ask fields. You never want to have an "enableAllInputFields", if you need to re-enable after calling this function,
 * you should use "setDisabledBasedOnQuote"
 */
const disableAllInputFields = (state: WritableDraft<ManualRFQPricingState>) => {
  state.form.compositeBidAsk.bid = state.form.compositeBidAsk.bid.setDisabled(true);
  state.form.compositeBidAsk.ask = state.form.compositeBidAsk.ask.setDisabled(true);
  state.form.compositeBidAsk.bidQty = state.form.compositeBidAsk.bidQty.setDisabled(true);
  state.form.compositeBidAsk.askQty = state.form.compositeBidAsk.askQty.setDisabled(true);

  state.form.legBidAsks.forEach(leg => {
    leg.bid = leg.bid.setDisabled(true);
    leg.ask = leg.ask.setDisabled(true);
  });
};

/**
 * Given the state.currentCustomerQuote status and side, will ensure our inputs are in the correct disabled-state.
 * Used whenever the currentCustomerQuote changes
 */
function setDisabledBasedOnQuote(state: WritableDraft<ManualRFQPricingState>) {
  const cq = state.currentCustomerQuote;
  if (cq == null) {
    disableAllInputFields(state);
    return;
  }

  const sizeCounteringEnabled = cq.NegotiationParameters?.SizeCountering === ModeEnum.Enabled;
  const { disableBidFields, disableAskFields } = getSidesToDisable(cq);
  state.form.compositeBidAsk.bid = state.form.compositeBidAsk.bid
    .setDisabled(disableBidFields)
    .setIsRequired(state.form.pricingMethod === 'composite' && !disableBidFields);
  state.form.compositeBidAsk.ask = state.form.compositeBidAsk.ask
    .setDisabled(disableAskFields)
    .setIsRequired(state.form.pricingMethod === 'composite' && !disableAskFields);

  state.form.compositeBidAsk.bidQty = state.form.compositeBidAsk.bidQty
    .setDisabled(disableBidFields)
    .setIsRequired(sizeCounteringEnabled && !disableBidFields);
  state.form.compositeBidAsk.askQty = state.form.compositeBidAsk.askQty
    .setDisabled(disableAskFields)
    .setIsRequired(sizeCounteringEnabled && !disableAskFields);

  state.form.legBidAsks?.forEach(leg => {
    leg.bid = leg.bid
      .setDisabled(disableBidFields)
      .setIsRequired(state.form.pricingMethod === 'per-leg' && !disableBidFields);
    leg.ask = leg.ask
      .setDisabled(disableAskFields)
      .setIsRequired(state.form.pricingMethod === 'per-leg' && !disableAskFields);
  });
}

function setPricingMethod(state: WritableDraft<ManualRFQPricingState>, pricingMethod: PricingMethod) {
  state.form.pricingMethod = pricingMethod;

  if (pricingMethod === 'composite') {
    // Switching to composite (top-down). Reset all leg bid-asks
    state.form.legBidAsks?.forEach(leg => {
      leg.bid = leg.bid.updateValue(undefined).setTouched(false).setIsRequired(false);
      leg.ask = leg.ask.updateValue(undefined).setTouched(false).setIsRequired(false);
    });

    // And make the composite bid-ask required
    state.form.compositeBidAsk.bid = state.form.compositeBidAsk.bid.setIsRequired(true);
    state.form.compositeBidAsk.ask = state.form.compositeBidAsk.ask.setIsRequired(true);
  }

  if (pricingMethod === 'per-leg') {
    // Reset the composite bid-ask
    state.form.compositeBidAsk.bid = state.form.compositeBidAsk.bid
      .updateValue(undefined)
      .setTouched(false)
      .setIsRequired(false);
    state.form.compositeBidAsk.ask = state.form.compositeBidAsk.ask
      .updateValue(undefined)
      .setTouched(false)
      .setIsRequired(false);

    // And make the leg bid-asks required
    state.form.legBidAsks?.forEach(leg => {
      leg.bid = leg.bid.setIsRequired(true);
      leg.ask = leg.ask.setIsRequired(true);
    });
  }
}

function setCompositePrice(
  state: WritableDraft<ManualRFQPricingState>,
  value: string | undefined,
  side: EditableSide,
  updatePricingMethod = true // set to false if there are more updates to be made in the same operation
) {
  if (updatePricingMethod) {
    setPricingMethod(state, 'composite');
  }
  state.form.compositeBidAsk[side] = state.form.compositeBidAsk[side].updateValue(value);
}

function setCompositeQty(
  state: WritableDraft<ManualRFQPricingState>,
  value: string | undefined,
  side: EditableSide,
  touch = false
) {
  const qtyFieldBySide: Record<EditableSide, 'bidQty' | 'askQty'> = {
    bid: 'bidQty',
    ask: 'askQty',
  };
  const qtyField = qtyFieldBySide[side];
  state.form.compositeBidAsk[qtyField] = state.form.compositeBidAsk[qtyField].updateValue(value).setTouched(touch);
}

function setLegPrice(
  state: WritableDraft<ManualRFQPricingState>,
  symbol: string,
  value: string | undefined,
  side: EditableSide,
  updatePricingMethod = true // set to false if there are more updates to be made in the same operation
) {
  const leg = state.form.legBidAsks?.find(leg => leg.symbol === symbol);
  if (!leg) {
    return;
  }

  if (updatePricingMethod) {
    setPricingMethod(state, 'per-leg');
  }
  leg[side] = leg[side].updateValue(value);
}

/**
 * When a new customer quote arrives, it might have prices set on it. If it does, and the price update is applicable, we go through
 * a few steps to apply it to the form. Sadly, this might overwrite changes the user is in the middle of making. However the intention of
 * this Manual RFQ form is very much that just one user should be editing it at once.
 */
function maybeUpdateFormWithPricesFromNewCustomerQuote(
  state: WritableDraft<ManualRFQPricingState>,
  quote: CustomerQuote
) {
  if (!quote.PricingMode) {
    // Nothing we can do... I also think this will always be defined, but not tackling this right now
    return;
  }
  // A new revision of the Quote has come in, and it might have relevant prices set on it. If it does, we want to write these into the input fields.
  const isActivelyBeingPriced = quote.QuoteStatus === QuoteStatusEnum.Open;
  if (!isActivelyBeingPriced) {
    // We don't care about this update.
    return;
  }

  // The setter functions are connected to fields, which call onChange with _display values_. So we need to reflect that here, too.
  const { toDisplayPrice } = resolvePricingModeDisplaySpec({ pricingMode: quote.PricingMode, security: undefined });

  setCompositePrice(state, toDisplayPrice(quote.BidPx), 'bid', false);
  setCompositePrice(state, toDisplayPrice(quote.OfferPx), 'ask', false);
  if (quote.BidQty != null) {
    setCompositeQty(state, quote.BidQty, 'bid', false);
  }
  if (quote.OfferQty != null) {
    setCompositeQty(state, quote.OfferQty, 'ask', false);
  }

  quote.LegSummary?.forEach(leg => {
    setLegPrice(state, leg.Symbol, toDisplayPrice(leg.BidPx), 'bid', false);
    setLegPrice(state, leg.Symbol, toDisplayPrice(leg.OfferPx), 'ask', false);
  });

  state.form.pricingMethod = getPricingMethodFromQuote(quote);
}

const manualRFQPricingSliceInstance = createSlice({
  name: 'manualRFQPricing',
  initialState: getInitialInstanceState(),
  reducers: {
    setCompositeBid: (state, action: PayloadAction<string>) => {
      setCompositePrice(state, action.payload, 'bid');
    },
    setCompositeAsk: (state, action: PayloadAction<string>) => {
      setCompositePrice(state, action.payload, 'ask');
    },
    setCompositeBidQty: (state, action: PayloadAction<string>) => {
      setCompositeQty(state, action.payload, 'bid', true);
    },
    setCompositeAskQty: (state, action: PayloadAction<string>) => {
      setCompositeQty(state, action.payload, 'ask', true);
    },
    setLegBid: (state, action: PayloadAction<{ symbol: string; value: string }>) => {
      setLegPrice(state, action.payload.symbol, action.payload.value, 'bid');
    },
    setLegAsk: (state, action: PayloadAction<{ symbol: string; value: string }>) => {
      setLegPrice(state, action.payload.symbol, action.payload.value, 'ask');
    },
    setAddToast: (state, action: PayloadAction<ManualRFQPricingState['addToast']>) => {
      state.addToast = action.payload;
    },
    prime: (state, { payload: quote }: PayloadAction<CustomerQuote>) => {
      state.currentCustomerQuoteRFQID = quote.RFQID;
      state.currentCustomerQuote = quote;
      state.initialized = true;
      state.isLoading = false;

      const { compositeBidAsk, legBidAsks, pricingMethod } = customerQuoteToBidAskFields(quote);

      state.form.compositeBidAsk = compositeBidAsk;
      state.form.legBidAsks = legBidAsks;
      state.form.pricingMethod = pricingMethod;
    },
    /** This is called by the ManualRFQPricingService. It keeps the primed CustomerQuote in sync with BE */
    setCurrentCustomerQuote: (state, { payload: cq }: PayloadAction<CustomerQuote>) => {
      state.currentCustomerQuote = cq;
      maybeUpdateFormWithPricesFromNewCustomerQuote(state, cq);
      setDisabledBasedOnQuote(state);
    },
    /**
     * Touch all is used by the view to bring forward all error states disabling the Confirm button
     * Given this, the touchAll action here should only set touched on the fields that are relevant for the current pricing method
     */
    touchAll: state => {
      state.form.compositeBidAsk.bidQty = state.form.compositeBidAsk.bidQty.setTouched(true);
      state.form.compositeBidAsk.askQty = state.form.compositeBidAsk.askQty.setTouched(true);

      if (state.form.pricingMethod === 'composite') {
        state.form.compositeBidAsk.bid = state.form.compositeBidAsk.bid.setTouched(true);
        state.form.compositeBidAsk.ask = state.form.compositeBidAsk.ask.setTouched(true);
      } else {
        state.form.legBidAsks.forEach(leg => {
          leg.bid = leg.bid.setTouched(true);
          leg.ask = leg.ask.setTouched(true);
        });
      }

      // Later when we add more normal fields, we want to add them all into this function too.
      // Other slices iterate over all keys in the `form` object and touch all BaseField instances. We should do the same when we have some of those.
    },

    setIsLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.isLoading = payload;
    },

    initState: () => {
      return getInitialInstanceState();
    },
    /** Clean reset. Called on close of the form. */
    resetState: () => {
      return getInitialInstanceState();
    },
  },
});

export const manualRFQPricingReducer = sliceToKeyedReducer(manualRFQPricingSliceInstance);

export function getRFQPricingActions(instanceID: string) {
  return {
    primeState: keyedActionWithInstanceID(manualRFQPricingSliceInstance.actions.prime, instanceID),
    setCurrentCustomerQuote: keyedActionWithInstanceID(
      manualRFQPricingSliceInstance.actions.setCurrentCustomerQuote,
      instanceID
    ),
    setCompositeBid: keyedActionWithInstanceID(manualRFQPricingSliceInstance.actions.setCompositeBid, instanceID),
    setCompositeAsk: keyedActionWithInstanceID(manualRFQPricingSliceInstance.actions.setCompositeAsk, instanceID),
    setCompositeBidQty: keyedActionWithInstanceID(manualRFQPricingSliceInstance.actions.setCompositeBidQty, instanceID),
    setCompositeAskQty: keyedActionWithInstanceID(manualRFQPricingSliceInstance.actions.setCompositeAskQty, instanceID),
    setLegBid: keyedActionWithInstanceID(manualRFQPricingSliceInstance.actions.setLegBid, instanceID),
    setLegAsk: keyedActionWithInstanceID(manualRFQPricingSliceInstance.actions.setLegAsk, instanceID),
    setIsLoading: keyedActionWithInstanceID(manualRFQPricingSliceInstance.actions.setIsLoading, instanceID),
    resetState: keyedActionWithInstanceID(manualRFQPricingSliceInstance.actions.resetState, instanceID),
    initState: keyedActionWithInstanceID(manualRFQPricingSliceInstance.actions.initState, instanceID),
    destroyState: keyedActionWithInstanceID(
      createDestroyKeyedStateAction(manualRFQPricingSliceInstance.name),
      instanceID
    ),
    touchAll: keyedActionWithInstanceID(manualRFQPricingSliceInstance.actions.touchAll, instanceID),
    setAddToast: keyedActionWithInstanceID(manualRFQPricingSliceInstance.actions.setAddToast, instanceID),
  };
}

/**
 * To be used if you want to prime the ManualRFQPricingSlice with a Quote, but you only have the RFQID at hand.
 * The thunk will create a ws request to get the full CustomerQuote, and then using that response call the normal prime action.
 * Depending on the type of CustomerQuote, it will either navigate to a pricing panel or the pricing drawer, so don't worry about that bit.
 */
const primeRFQIDThunk = createAsyncThunk<
  void,
  { rfqID: string; navigate: NavigateFunction; layoutId: string },
  { state: AppState; extra: AppExtraArgument }
>('manualRFQPricingSlice/primeRFQIDThunk', async ({ rfqID, ...params }, thunk) => {
  try {
    const request = thunk.dispatch(streamingDataSlice.endpoints.getCustomerQuotes.initiate({ RFQID: rfqID }));
    const result = await request.unwrap();
    request.unsubscribe(); // now that we have the data, immediately unsubscribe. we do not need updates.

    const quote = result.get(rfqID);
    invariant(quote != null, 'No quote found');

    thunk.dispatch(primeThunk({ quote, ...params })); // hand the rest of the work over to the main priming thunk
  } catch (error) {
    logger.error(new Error(`Failed to fetch CustomerQuote for RFQID`), { extra: { rfqID } });
    globalToastsDispatch(
      globalToastActions.addToastWithTimeout({
        type: 'ui',
        value: {
          text: 'Failed to find Quote',
          variant: 'NEGATIVE',
          id: uuid(),
          timeout: 5000,
        },
      })
    );
    throw error; // Re-throw to let Redux Toolkit handle the rejected state properly
  }
});

/**
 * To be used if you want to prime the Manual RFQ Pricing form with some CustomerQuote. The thunk will also either
 * navigate to your pricing panel, or open the pricing drawer for you.
 */
const primeThunk = createAsyncThunk<
  void,
  { quote: CustomerQuote; navigate: NavigateFunction; layoutId: string },
  { state: AppState; extra: AppExtraArgument }
>('manualRFQPricingSlice/primeThunk', async ({ quote, navigate, layoutId }, thunk) => {
  if (quote.LegSummary) {
    const instanceID = quote.RFQID; // for panels, we are running with the RFQID as the state instanceID
    // prime the slice instance, and then navigate to the panel
    thunk.dispatch(getRFQPricingActions(instanceID).primeState(quote));
    navigate(getManualRFQPricingRoute({ rfqID: quote.RFQID, layoutId }));
  } else {
    // Else we are a non-compound CustomerQuote. For these, we use the singleton pricing drawer.
    // There is only one instance of the drawer, so the instanceID is a hardcoded string in the frontend.
    const instanceID = DRAWER_RFQ_PRICING_INSTANCE_ID;
    thunk.dispatch(getRFQPricingActions(instanceID).primeState(quote));
    thunk.dispatch(OMSSliceActions.openView(OMSView.ManualRFQPricingForm)); // instead of navigating to a panel, we just open the
  }
});

export const manualRFQPricingThunks = {
  primeRFQID: primeRFQIDThunk,
  prime: primeThunk,
};
